import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Authorization } from 'src/app/authorization/authorization';
import { AuthorizationService } from 'src/app/authorization/authorization.service';
import { isUserLicensed } from 'src/app/common/common-functions';
import { NavigationConstants } from 'src/app/common/navigation-constants';
import { UserStateService } from 'src/app/user-state/user-state.service';
import { MenuItem } from '../../menu-item';

/**
 * This component displays the menu sections and it's related nested menu items.
 */
@Component({
	selector: 'crownx-menu-items',
	templateUrl: './menu-items.component.html',
	styleUrls: ['./menu-items.component.css']
})
export class MenuItemsComponent implements OnInit, OnDestroy {
	/** The list of parent menu items that the user can see. */
	public ParentMenuItemsToShow: MenuItem[] = [];

	/** The user's roles. */
	private userRoles: string[] = [];

	/** The user's authorization. */
	private userAuthorization: Authorization | null = null;

	/** Used to unsubscribe from observables. */
	private unsubscriber = new Subject();

	/** Indicates if the hamburger menu is open. */
	@Input() IsHamburgerMenuOpen: boolean = false;

	/** Indicates the menu action triggered. */
	@Output() MenuItemActionTriggered: EventEmitter<void> = new EventEmitter<void>();

	/**
	 * @constructor
	 *
	 * @param authorizationService The authorization service.
	 * @param userStateService The service used to change and get the user state.
	 */
	constructor(
		private readonly authorizationService: AuthorizationService,
		private readonly userStateService: UserStateService
	) { }

	/**
	 * Component initialization.
	 */
	public ngOnInit(): void {
		this.authorizationService.OnGetAuthorization$()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe((auth: Authorization | null) => {
				this.userRoles = auth?.Roles ?? [];
				this.userAuthorization = auth;
				this.PopulateMenuItems();
			});
	}

	/**
	 * Component deconstruction.
	 */
	public ngOnDestroy(): void {
		this.unsubscriber.next(null);
		this.unsubscriber.complete();
	}

	/**
	 * Populate menu items based on the user's authorizations.
	 */
	public PopulateMenuItems(): void {
		this.ParentMenuItemsToShow = [];
		const agentSelected: boolean = Boolean(this.userStateService.State?.CrownIdentifier);

		NavigationConstants.NAVIGATION_MENU_ITEMS.forEach((parentMenuItem: MenuItem) => {
			if (!parentMenuItem.ChildMenuItems) {
				if(this.IsMenuItemVisible(parentMenuItem, agentSelected)) {
					this.ParentMenuItemsToShow.push({ ...parentMenuItem, ChildMenuItems: null });
				}
			}
			else {
				const filteredMenuItems: MenuItem[] = parentMenuItem.ChildMenuItems.filter((menuItem: MenuItem) =>
					this.IsMenuItemVisible(menuItem, agentSelected)
				);

				if (filteredMenuItems.length > 0) {
					this.ParentMenuItemsToShow.push({ ...parentMenuItem, ChildMenuItems: filteredMenuItems });
				}
			}
		});
	}

	/**
	 * Determines if the menu items should be displayed to users.
	 * @param menuItem The menu item.
	 * @param agentSelected Boolean if an agents has been selected or not.
	 * @returns True if the menu item should be displayed to the user.
	 */
	private IsMenuItemVisible(menuItem: MenuItem, agentSelected: boolean): boolean {
		if (menuItem.Role != null && this.userRoles.filter((role: string) => role == menuItem.Role).length < 1)
			return false;

		if (menuItem.License != null && this.userAuthorization != null && !isUserLicensed(this.userAuthorization, menuItem.License))
			return false;

		if (menuItem.AgentRequiredToShow && !agentSelected)
			return false;

		// Hide menu items on mobile devices if ShowOnMobile is explicitly set to false
		if (menuItem.ShowOnMobile === false && this.IsMobileDevice())
			return false;

		return true;
	}

	/**
	 * Determines if the current device is a mobile device based on screen width.
	 * @returns True if the device is considered mobile (screen width <= 1280px).
	 */
	private IsMobileDevice(): boolean {
		return window.innerWidth <= 1280;
	}
}
